import {
  EuiBetaBadge,
  EuiButton,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPageHeader,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { WCICTextLabel } from "components";
import { useAPIURL } from "hooks/useAPIURL";
import moment from "moment";
import { Fragment } from "react";
import NumberFormat from "react-number-format";
import { useNavigate } from "react-router-dom";
import { HOForm, Quote } from "types/apiTypes";

import { FormDataType } from "../components/FormDataTypes";
import ShowRateNumberStat from "../components/ShowRateNumberStat";
import { LPQuoteInput } from "../types/Rater.types";
import { useFormsData } from "./hooks/useFormsData";

type LandlordsViewQuoteProps = {
  quote: Quote;
};

function valuationFormat(valuation: string) {
  switch (valuation) {
    case "RC":
      return "Replacement Cost Estimate";
    case "ACV":
      return "Actual Cash Value";
    case "IACV":
      return "Indexed Actual Cash Value";
  }
}

function perilsFormat(peril: string) {
  switch (peril) {
    case "FL1R":
      return "FL-1R";

    case "FL1RV":
      return "FL-1RV";

    case "FL2":
      return "FL-2";

    case "FL3":
      return "FL-3";

    default:
      break;
  }
}

function simpleNumber(num: string) {
  return (
    <NumberFormat
      value={num}
      displayType="text"
      thousandSeparator={true}
      prefix={"$"}
      renderText={(num) => {
        return <>{num}</>;
      }}
    />
  );
}

const LandlordsViewQuote = ({ quote }: LandlordsViewQuoteProps) => {
  const navigate = useNavigate();

  const quoteDetails = quote.details as LPQuoteInput;
  const { formsData } = useFormsData(quoteDetails.effectiveDate);
  const processedForms = quoteDetails.locations.map((location) => {
    return {
      title: location.streetAddress + " " + location.city + ", " + location.state + " " + location.zip,
      forms: location.forms.map((form) => {
        return { ...form, rate: quote.rate!.form_premium[form.formID] };
      }),
    };
  });
  const defaultURL = useAPIURL();

  return (
    <>
      <EuiPageHeader iconType="home" pageTitle={`Landlords Quote ${quote.recordID}`} />
      <EuiFlexGroup wrap>
        <EuiFlexItem grow={2}>
          <EuiPanel>
            <EuiTitle>
              <h3>Applicant</h3>
            </EuiTitle>
            <WCICTextLabel label="Name">
              <EuiText style={{ paddingLeft: "10px" }}>{`${quoteDetails.firstName} ${quoteDetails.lastName}`}</EuiText>
            </WCICTextLabel>

            <WCICTextLabel label="Mailing Address">
              <EuiText style={{ paddingLeft: "10px" }}>{`${quoteDetails.streetAddress}`}</EuiText>
              <EuiText style={{ paddingLeft: "10px" }}>{`${quote.city}, ${quote.state} ${quote.zip}`}</EuiText>
            </WCICTextLabel>
          </EuiPanel>
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiPanel>
            <WCICTextLabel label="Rate">
              <EuiText style={{ paddingLeft: "10px" }}>{simpleNumber(`${quote.totalPremium}`)}</EuiText>
            </WCICTextLabel>
            <WCICTextLabel label="Quote Created">
              <EuiText style={{ paddingLeft: "10px" }}>{moment(`${quote.timestamp}`).format("lll")}</EuiText>
            </WCICTextLabel>
            <EuiSpacer />
            <EuiButton
              onClick={() => {
                window.open(`${defaultURL}/print-quote/${quote.recordID}`, "_blank");
              }}
            >
              Print
            </EuiButton>
            <EuiSpacer />
            <EuiButton onClick={() => navigate(`/rating/${quote.recordID}`)}>Edit</EuiButton>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer />

      {quoteDetails.locations.map((location) => {
        return (
          <Fragment key={location.streetAddress}>
            <EuiPanel>
              <EuiTitle>
                <h3>Coverages</h3>
              </EuiTitle>
              <EuiFlexGroup>
                <EuiFlexItem grow={2}>
                  <WCICTextLabel label="Location">
                    <EuiText style={{ paddingLeft: "10px" }}>{`${location.streetAddress}`}</EuiText>
                    <EuiText
                      style={{ paddingLeft: "10px" }}
                    >{`${location.city}, ${location.state} ${location.zip}`}</EuiText>
                  </WCICTextLabel>
                </EuiFlexItem>
                <EuiFlexItem grow={3}>
                  <WCICTextLabel label="Property Coverages">
                    <EuiText style={{ paddingLeft: "10px" }}>Perils: {perilsFormat(`${location.perils}`)}</EuiText>
                    {/* Should have a conditional to show percentage if applicable... what kind of format? */}
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Valuation: {valuationFormat(`${location.valuation}`)}
                    </EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Dwelling Limit (Coverage A): {simpleNumber(`${location.covA}`)}
                    </EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Related Structures (Coverage B): {simpleNumber(`${location.covB}`)}
                    </EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Contents (Coverage C): {simpleNumber(`${location.covC}`)}
                    </EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Loss of Rents (Coverage D): {simpleNumber(`${location.covD}`)}
                    </EuiText>
                  </WCICTextLabel>
                </EuiFlexItem>
                <EuiFlexItem grow={2}>
                  <WCICTextLabel label="Liability Coverage">
                    <EuiText style={{ paddingLeft: "10px" }}>Liability Form: {`${quoteDetails.liabilityForm}`}</EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>
                      Liability: {simpleNumber(`${quoteDetails.liability}`)}
                    </EuiText>
                    <EuiText style={{ paddingLeft: "10px" }}>Medpay: {simpleNumber(`${quoteDetails.medpay}`)}</EuiText>
                  </WCICTextLabel>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>
            <EuiSpacer />
          </Fragment>
        );
      })}

      <EuiSpacer />
      {processedForms.map((location) => {
        return (
          <Fragment key={location.title}>
            <EuiPanel>
              <EuiTitle>
                <h3>Additional Forms - {location.title}</h3>
              </EuiTitle>
              <EuiSpacer />
              <EuiFlexGroup direction="column">
                {location.forms.map((form) => {
                  return <QuoteFormView key={form.formID} form={form} formsData={formsData} />;
                })}
              </EuiFlexGroup>
            </EuiPanel>
            <EuiSpacer />
          </Fragment>
        );
      })}
    </>
  );
};

export default LandlordsViewQuote;

interface HOFromWithRate extends HOForm {
  rate: number;
}

interface QuoteFormViewProps {
  form: HOFromWithRate;
  formsData: Record<string, FormDataType>;
}

const QuoteFormView = ({ form, formsData }: QuoteFormViewProps) => {
  const formData = formsData[form.name];
  if (!formData) return null;

  const fieldKeys = Object.keys(form.fields || {});
  const listItems = fieldKeys.map((fieldName) => {
    const fieldInfo = formData.inputs.find((field) => field.name === fieldName);
    if (fieldInfo?.type === "number") {
      return {
        title: fieldInfo.label,
        description: form.fields[fieldName],
      };
    }
    if (fieldInfo?.type === "money") {
      return {
        title: fieldInfo.label,
        description: simpleNumber(form.fields[fieldName]),
      };
    }
    if (fieldInfo?.type === "select") {
      const selectionText = fieldInfo?.options?.find((option) => option.value === form.fields[fieldName]);
      return {
        title: fieldInfo.label,
        description: selectionText?.text || "",
      };
    }

    return {
      title: "Default",
      description: "Return",
    };
  });

  return (
    <EuiFlexItem>
      <EuiPanel className="wcicPolicyForm wcicPolicyForm--hasBetaBadge" paddingSize="l" color="subdued">
        <EuiFlexGroup gutterSize="l" direction="row">
          <EuiFlexItem grow={false} style={{ marginRight: "0px" }}>
            <EuiIcon size="xl" type={"reportingApp"} style={{ marginRight: "16px" }} />
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <EuiTitle size="s">
              <span>{formData.formTitle}</span>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={1}>
            <EuiDescriptionList listItems={listItems} textStyle={"reverse"} />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup direction="column" alignItems="flexEnd">
              <EuiFlexItem grow={false}>
                <ShowRateNumberStat data={form.rate} isLoading={false} valid={true} description="Premium" />
                {/* <PremiumStat formID={formID} /> */}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
        <span className="wcicPolicyForm__betaBadgeWrapper">
          {formData.policyForm ? (
            <EuiBetaBadge
              label={"label"}
              className="wcicPolicyForm__betaBadge wcicPolicyForm__betaBadge--blue"
              tooltipContent={<span>This form applies to all locations.</span>}
              color="subdued"
            />
          ) : (
            <EuiBetaBadge label={formData.displayFormName} className="wcicPolicyForm__betaBadge " />
          )}
        </span>
      </EuiPanel>
    </EuiFlexItem>
  );
};
